import { summerWeeks } from '../../../mocks/camps';

interface SummerCalendarProps {
  selectedWeek: number | null;
  onWeekSelect: (week: number | null) => void;
}

const monthColors: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  Jun: { bg: 'bg-pink-50',   text: 'text-pink-700',   border: 'border-l-pink-500',  dot: 'bg-pink-500'   },
  Jul: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-l-yellow-500', dot: 'bg-yellow-500' },
  Aug: { bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-l-amber-500',  dot: 'bg-amber-500'  },
};

function getMonth(dates: string): string {
  if (dates.startsWith('Jun')) return 'Jun';
  if (dates.startsWith('Jul') || dates.includes('Jul')) return 'Jul';
  return 'Aug';
}

export default function SummerCalendar({ selectedWeek, onWeekSelect }: SummerCalendarProps) {
  const handleClick = (week: number) => {
    onWeekSelect(selectedWeek === week ? null : week);
  };

  return (
    <section id="plan-summer" className="py-16 bg-gradient-to-b from-pink-50/30 via-yellow-50/20 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-600 to-pink-500 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-4">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-2-line text-sm"></i>
            </div>
            SUMMER 2026
          </div>
          <h2 className="text-4xl font-bold text-purple-900 mb-3">Plan Your Summer</h2>
          <p className="text-gray-600 text-base max-w-xl mx-auto">
            Click any week below to instantly see which camps are available during that time. Plan ahead and lock in your child's perfect summer.
          </p>
        </div>

        {/* Month legend */}
        <div className="flex items-center justify-center gap-6 mb-8">
          {['Jun', 'Jul', 'Aug'].map(m => {
            const c = monthColors[m];
            return (
              <div key={m} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${c.dot}`}></div>
                <span className="text-sm font-semibold text-gray-700">{m === 'Jun' ? 'June' : m === 'Jul' ? 'July' : 'August'}</span>
              </div>
            );
          })}
        </div>

        {/* Week grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-6">
          {summerWeeks.map(({ week, label, dates }) => {
            const month = getMonth(dates);
            const c = monthColors[month];
            const isSelected = selectedWeek === week;

            return (
              <button
                key={week}
                onClick={() => handleClick(week)}
                className={`relative group rounded-2xl p-5 text-left transition-all duration-200 cursor-pointer whitespace-nowrap border-l-4
                  ${isSelected
                    ? 'bg-gradient-to-br from-pink-600 to-pink-500 shadow-lg shadow-pink-500/30 scale-105 border-l-yellow-400'
                    : `bg-white hover:shadow-md hover:scale-102 ${c.border}`
                  }`}
              >
                {/* Selected indicator */}
                {isSelected && (
                  <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-white/20 flex items-center justify-center">
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                )}

                {/* Month dot */}
                {!isSelected && (
                  <div className={`w-2.5 h-2.5 rounded-full ${c.dot} mb-3`}></div>
                )}

                <div className={`text-xs font-bold uppercase tracking-wide mb-1 ${isSelected ? 'text-white/90' : 'text-gray-500'}`}>
                  {label}
                </div>
                <div className={`text-sm font-bold leading-snug ${isSelected ? 'text-white' : 'text-gray-800'}`}>
                  {dates}
                </div>
              </button>
            );
          })}
        </div>

        {/* Active week banner */}
        {selectedWeek !== null && (
          <div className="flex items-center justify-between bg-white border-l-4 border-l-pink-500 rounded-2xl px-6 py-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-600 to-pink-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-calendar-check-line text-white text-lg"></i>
              </div>
              <div>
                <div className="text-sm font-bold text-gray-800">
                  Showing camps available during{' '}
                  <span className="text-pink-600">
                    {summerWeeks.find(w => w.week === selectedWeek)?.label} ({summerWeeks.find(w => w.week === selectedWeek)?.dates})
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">Scroll down to see matching camps</div>
              </div>
            </div>
            <button
              onClick={() => onWeekSelect(null)}
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-pink-600 transition-colors cursor-pointer whitespace-nowrap ml-4"
            >
              <i className="ri-close-line text-base"></i>
              Clear week
            </button>
          </div>
        )}
      </div>
    </section>
  );
}